import { Navigate, Outlet } from 'react-router-dom';

import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { useSession } from '@/compositions/hooks/useSession';

/**
 * Guarda das telas de cadastro: quem já tem uma conta anônima não cadastra de
 * novo — termina a que já existe (DV-24).
 *
 * ## O beco que ela fecha
 *
 * Depois de pedir socorro sem cadastro, a pessoa **está** com sessão: anônima,
 * mas sessão, com perfil gravado e um pedido de socorro debaixo dele. Se dali
 * ela abrisse o cadastro comum, `createAccount` chamaria `signUp` do zero e o
 * caminho quebraria das duas formas possíveis — convertendo a conta anônima, a
 * gravação do perfil esbarraria na linha de `profiles` que já existe para
 * aquele `id`; abrindo um usuário novo, o pedido de socorro ficaria órfão na
 * conta abandonada. Nos dois casos a pessoa veria um erro genérico logo depois
 * de ter pedido resgate.
 *
 * `/register/finish` é onde o cadastro dela **de fato** continua: mesmo `id`,
 * pedido preservado, e os dois estados possíveis (e-mail por confirmar, e-mail
 * já confirmado) tratados lá.
 *
 * ## Por que guarda de rota, e não uma checagem por página
 *
 * Porque são várias telas — a escolha de objetivo e cada formulário de
 * cadastro — e a regra é uma só. Repetida em cada página, ela vira quatro
 * cópias que precisam concordar para sempre, e a primeira tela de cadastro
 * criada depois desta nasce sem ela. Guarda de rota é como `RequireSession` e
 * `RequireActiveAccount` já resolvem o mesmo tipo de pergunta.
 *
 * ⚠️ **`/register/finish` fica de fora dela**, obviamente: é o destino. Montar o
 * `finish` dentro desta guarda faria um laço de redirecionamento.
 *
 * ⚠️ Ela **não** protege contra cadastro duplicado de quem tem conta completa —
 * essa pergunta é outra e não é de segurança: quem já entrou e abre `/register`
 * está pedindo outra conta, o que o Supabase recusa com "e-mail já em uso".
 */
export function RequireNotAnonymous() {
  const { isAnonymous, isLoading } = useSession();

  // Sem o estado de carregamento, o primeiro quadro decide com a sessão ainda
  // não lida e deixa passar quem deveria ser redirecionada — o mesmo cuidado
  // das outras guardas do módulo.
  if (isLoading) return <ArcusLoading className="h-dvh" />;

  if (isAnonymous) return <Navigate to="/register/finish" replace />;

  return <Outlet />;
}
